import React, { useEffect, useState } from 'react';

// Components
import DataSourceTabs from '@/views/components/data-source/DataSourceTabs';
import { LoadingSpinner } from '@/views/components/common';
import { GenericEmptyState } from '@/views/components/misc';
import { PortfoliosTableV2 } from '@/views/components/tables';

// Enums
import { DATA_SOURCE_TAB_CATEGORY } from '@/enums/DataSource';
import { PERSISTENCE_KEYS, resetLocalStoragePersistenceSource } from '@/utils/persistence';

// Hooks
import useThemeVersion from '@/hooks/useThemeVersion';

// Legacy Components
import LegacyContactPortfoliosList from '@/views/components/modals/modal-content/LegacyContactPortfoliosList';

// Libraries
import classNames from 'classnames';
import { ColumnSort } from '@tanstack/react-table';
import { get } from 'lodash';
import { SortingRule } from 'react-table';

// Types
import User from '@/types/user';
import Portfolio from '@/types/portfolio';
import { DataSourceTabs as DataSourceTabsType } from '@/types/app-config';
import { PORTFOLIO_METRICS_FIELDS, PORTFOLIO_TABLE_COLUMNS } from '@/enums/Portfolio';

// Utils
import { formatMessage } from '@/utils/i18n';
import { getPortfolioMissingOrInvalidHoldings } from '@/utils/portfolio/utils';
import { userHasPermissionEnabled, PERMISSIONS, userHasPermission } from '@/utils/permissions';
import localStorageUtils from '@/utils/local-storage';
import { getFilteredDataSourceTabs } from '@/utils/data-source';

type Props = {
  user: User;
  emptyStateHeader: string;
  isLoading: boolean;
  searchQuery: string;
  selectedContact: any;
  serverSideSorting?: boolean;
  sortedColumns?: ColumnSort[];
  isPostMessageActive: boolean;
  onLoadMorePortfolios: () => void;
  onRowSelectionChange?: (selectedRows: Portfolio[]) => void;
  onSearchPortfolios: (query: string) => void;
  onServerSideSorting?: (sort: ColumnSort) => void;
  setIsActivePortfolioPostMessage: (isActive: boolean) => void;
  setPersistenceSource: (user: User, key: string, value?: string) => void;
};

type LegacyProps = {
  backButtonText: string;
  onBack: () => void;
  isThemeLight?: boolean;
  onSelectPortfolios: (portfolios: Portfolio[]) => void;
  onSortPortfolios: (sortingRules: SortingRule[]) => void;
  onUnselectPortfolios: (portfolios: Portfolio[]) => void;
};

const getDisplayedColumns = (user: User, dataSourceTabs: DataSourceTabsType[] | null, activeDataSource: string) => {
  const isPostMessageTab = dataSourceTabs?.find(tab => tab.value === activeDataSource)?.isPostMessage || false;
  const isPersistenceSourceTab = dataSourceTabs?.find(tab => tab.value === activeDataSource)?.persistenceKey || false;

  return [
    {
      key: PORTFOLIO_TABLE_COLUMNS.NAME,
    },
    {
      key: PORTFOLIO_TABLE_COLUMNS.ID,
      hidden: !userHasPermission(user, PERMISSIONS.PORTFOLIOS_SHOW_PORTFOLIO_ID) || (!isPostMessageTab && !isPersistenceSourceTab),
    },
    {
      key: PORTFOLIO_TABLE_COLUMNS.PLAN_ACCOUNT,
      hidden: !userHasPermissionEnabled(user, PERMISSIONS.PLANNING),
    },
    {
      key: PORTFOLIO_TABLE_COLUMNS.RISK_PROFILE,
    },
    {
      key: PORTFOLIO_TABLE_COLUMNS.LOCK,
    },
    {
      key: PORTFOLIO_TABLE_COLUMNS.SHOW_DETAILS,
      hidden: !userHasPermissionEnabled(user, PERMISSIONS.SHOW_PORTFOLIO_DETAILS),
    },
  ]
    .filter(col => !col.hidden)
    .map(col => col.key);
};

const ContactPortfoliosListV2 = ({
  user,
  emptyStateHeader,
  isLoading,
  searchQuery,
  selectedContact,
  serverSideSorting,
  sortedColumns,
  isPostMessageActive,
  onLoadMorePortfolios,
  onSearchPortfolios,
  onRowSelectionChange,
  onServerSideSorting,
  setIsActivePortfolioPostMessage,
  setPersistenceSource,
}: Props) => {
  const dataSourceTabs: DataSourceTabsType[] | null = getFilteredDataSourceTabs(user, DATA_SOURCE_TAB_CATEGORY.PORTFOLIOS, isPostMessageActive);
  const displayedMetrics = [
    PORTFOLIO_METRICS_FIELDS.MER,
    PORTFOLIO_METRICS_FIELDS.YEAR_ONE,
    PORTFOLIO_METRICS_FIELDS.YEAR_THREE,
    PORTFOLIO_METRICS_FIELDS.SIZE,
    PORTFOLIO_METRICS_FIELDS.HOLDINGS,
  ];
  const isTableHidden = isLoading || (selectedContact && (!selectedContact.portfolios || selectedContact.portfolios.length === 0));

  const [activeDataSource, setActiveDataSource] = useState<string>(dataSourceTabs?.[0]?.value ?? '');
  const displayedColumns = getDisplayedColumns(user, dataSourceTabs, activeDataSource);

  useEffect(() => {
    // Reset the Persistence Source when the component mounts
    const isPostMessageDataSourceTabExist = dataSourceTabs?.find(tab => tab.isPostMessage);
    const isInitialDataSourceTabPostMessage = isPostMessageActive && isPostMessageDataSourceTabExist;

    if (isInitialDataSourceTabPostMessage) {
      setIsActivePortfolioPostMessage(false);
    }
    resetLocalStoragePersistenceSource(localStorageUtils.STORAGE_KEYS.CONTACT_PORTFOLIO_PERSISTENCE_SOURCE, user, () => onSearchPortfolios(searchQuery));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPostMessageActive, searchQuery, user, onSearchPortfolios, setIsActivePortfolioPostMessage]);

  const renderEmptyMessage = () => <GenericEmptyState header={emptyStateHeader} />;

  const renderNoResultsMessage = () => (
    <GenericEmptyState
      header={formatMessage('EmptyState.NoResults.Title')}
      body={formatMessage('EmptyState.NoResults.Text')}
      actionLabel={formatMessage('EmptyState.NoResults.ClearSearch')}
      onAction={() => onSearchPortfolios('')}
    />
  );

  const renderNoResults = () => {
    if (searchQuery) {
      return renderNoResultsMessage();
    }

    return renderEmptyMessage();
  };

  const onSelectDataSource = (dataSourceValue: string) => {
    if (!dataSourceTabs) return;

    setActiveDataSource(dataSourceValue);

    const activeDataSource = dataSourceTabs.find(tab => tab.value === dataSourceValue);
    let persistenceSource;

    if (activeDataSource?.isPostMessage) {
      setIsActivePortfolioPostMessage(true);
    } else {
      setIsActivePortfolioPostMessage(false);
      persistenceSource = activeDataSource?.persistenceKey;
    }

    setPersistenceSource(user, localStorageUtils.STORAGE_KEYS.CONTACT_PORTFOLIO_PERSISTENCE_SOURCE, persistenceSource);
    onSearchPortfolios(searchQuery);
  };

  // Data Source Tabs related logic
  const isContactPersistenceSourceActive = localStorageUtils.getPersistenceSource(user, localStorageUtils.STORAGE_KEYS.CONTACT_PERSISTENCE_SOURCE) != null;
  const contactDataSourceTabs: DataSourceTabsType[] = get(user, 'appConfig.dataSourceTabs.contacts', []);
  const hasExternalContacts =
    !contactDataSourceTabs.filter(tab => tab.persistenceKey === PERSISTENCE_KEYS.EXISTING_CLIENT_CONTACT).length || isContactPersistenceSourceActive;

  return (
    <div className="contact-portfolio-list-v2">
      {dataSourceTabs && hasExternalContacts && (
        <div className="tabs-container">
          <DataSourceTabs disabled={isLoading} tabs={dataSourceTabs} value={activeDataSource} onChange={onSelectDataSource} />
        </div>
      )}

      {isLoading && (
        <div className="loading-spinner-container">
          <LoadingSpinner className="initial-loading" message={formatMessage('ContactPortfoliosModal.LoadingPortfolios')} />
        </div>
      )}

      {!isLoading &&
        (selectedContact.portfolios && selectedContact.portfolios.length === 0 ? (
          renderNoResults()
        ) : (
          <PortfoliosTableV2<Portfolio>
            className={classNames({ 'visually-hidden': isTableHidden })}
            displayedColumns={displayedColumns}
            displayedMetrics={displayedMetrics}
            forceSelectable
            onLoadMore={onLoadMorePortfolios}
            portfolios={selectedContact.portfolios}
            selectedTitle="PortfoliosTable.SelectedTitle.Portfolios"
            hasMore={selectedContact.hasMore.portfolios}
            canEditPredicate={portfolio =>
              userHasPermission(user, PERMISSIONS.PORTFOLIOS_EDIT) && (!portfolio.availableActions || portfolio.availableActions.edit)
            }
            canClonePredicate={portfolio =>
              userHasPermission(user, PERMISSIONS.PORTFOLIOS_CLONE) && (!portfolio.availableActions || portfolio.availableActions.clone)
            }
            missingProductPredicate={portfolio => getPortfolioMissingOrInvalidHoldings(portfolio).length > 0}
            onRowSelectionChange={onRowSelectionChange}
            serverSideSorting={serverSideSorting}
            onServerSideSorting={sort => onServerSideSorting && onServerSideSorting(sort)}
            sortedColumns={sortedColumns}
          />
        ))}
    </div>
  );
};

const ContactPortfoliosList = (props: Props & LegacyProps) => {
  const { isLightVersion } = useThemeVersion();
  if (isLightVersion) {
    return <ContactPortfoliosListV2 {...props} />;
  }
  return <LegacyContactPortfoliosList {...props} />;
};

export default ContactPortfoliosList;
